/**
 * Hook mejorado para WebSocket de supervisión con reconexión automática
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Estados de conexión
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

export const useSupervisionWebSocketV2 = (sessionId, token, mode = 'instructor') => {
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [vitals, setVitals] = useState(null);
  const [patientState, setPatientState] = useState(null);
  const [supervisionMode, setSupervisionMode] = useState('observador');
  const [hasControl, setHasControl] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState({ instructors: [], students: [] });
  const [timeline, setTimeline] = useState([]);
  // v10.5.0 regresión fix: arrays LIVE de medicamentos, acciones y exámenes
  // administrados por el alumno. Se pobla desde `action_performed` con
  // payload estructurado. Se fusiona con `sessionData.activity_area.*` en
  // el UI para que la pestaña Medicamentos/Acciones se actualice sin F5.
  const [liveMedications, setLiveMedications] = useState([]);
  const [liveActions, setLiveActions] = useState([]);
  const [liveExams, setLiveExams] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [latency, setLatency] = useState(0);
  const [lastSequenceNumber, setLastSequenceNumber] = useState(0);
  // v10.2.4: student control requests. The instructor UI shows a pill/badge
  // on top of the panel while `pendingControlRequest` is truthy.
  const [pendingControlRequest, setPendingControlRequest] = useState(null);
  // v10.3.1 Fase 2: transición progresiva actualmente activa
  const [activeVitalsTransition, setActiveVitalsTransition] = useState(null);
  // v10.8.1 — Últimos eventos XR (marcadores, zonas, procedimientos)
  const [xrEvents, setXrEvents] = useState([]);
  const [lastXrEvent, setLastXrEvent] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const pingTimestamp = useRef(null);
  const lastSeqRef = useRef(0);

  // Construir URL del WebSocket
  const getWebSocketUrl = useCallback(() => {
    if (!sessionId || !token) return null;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    // Cross-pod fix (RCA §48): include `last_seq` so server replays events
    // we missed while our socket was on a different pod.
    return `${wsProtocol}//${wsHost}/api/ws/simulation/${sessionId}?token=${token}&mode=${mode}&last_seq=${lastSeqRef.current || 0}`;
  }, [sessionId, token, mode]);

  // Enviar mensaje por WebSocket
  const sendMessage = useCallback((type, data = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...data }));
      return true;
    }
    console.warn('WebSocket not connected, message not sent:', type);
    return false;
  }, []);

  // Enviar mensaje de chat
  const sendChatMessage = useCallback((content, resourceUrl = null) => {
    return sendMessage('chat', { content, resource_url: resourceUrl });
  }, [sendMessage]);

  // Enviar pista al estudiante
  const sendHint = useCallback((content, hintType = 'sugerencia') => {
    return sendMessage('hint', { content, hint_type: hintType });
  }, [sendMessage]);

  // Tomar control de la simulación
  const takeControl = useCallback(() => {
    return sendMessage('take_control');
  }, [sendMessage]);

  // Liberar control
  const releaseControl = useCallback(() => {
    return sendMessage('release_control');
  }, [sendMessage]);

  // Cambiar modo de supervisión
  const setMode = useCallback((newMode) => {
    return sendMessage('set_mode', { mode: newMode });
  }, [sendMessage]);

  // Solicitar información de sesión
  const requestSessionInfo = useCallback(() => {
    return sendMessage('get_session_info');
  }, [sendMessage]);

  // Solicitar sincronización completa del estado
  const requestStateSync = useCallback(() => {
    return sendMessage('request_state_sync');
  }, [sendMessage]);

  // Enviar actualización de vitales (como instructor con control)
  const sendVitalsUpdate = useCallback((vitalsData, reason = '', transitionDurationMs = 0) => {
    // v10.3.1 Fase 2: si transitionDurationMs > 0, el backend broadcasta
    // metadata de transición y el alumno interpola suavemente.
    const payload = { vitals: vitalsData, reason };
    if (transitionDurationMs && transitionDurationMs > 0) {
      payload.transition_duration_ms = Math.floor(transitionDurationMs);
    }
    return sendMessage('update_vitals', payload);
  }, [sendMessage]);

  // Enviar acción (como instructor con control)
  const sendAction = useCallback((actionData) => {
    return sendMessage('action', actionData);
  }, [sendMessage]);

  // Pausar simulación (requiere control)
  const pauseSimulation = useCallback((reason = '') => {
    return sendMessage('pause', { reason });
  }, [sendMessage]);

  // Reanudar simulación
  const resumeSimulation = useCallback(() => {
    return sendMessage('resume');
  }, [sendMessage]);

  // Finalizar simulación
  const endSimulation = useCallback((notes = '') => {
    return sendMessage('end_simulation', { notes });
  }, [sendMessage]);

  // Activar evento clínico
  const triggerEvent = useCallback((eventName, effects = {}) => {
    return sendMessage('trigger_event', { event_name: eventName, effects });
  }, [sendMessage]);

  // Obtener user ID del token
  const getCurrentUserId = useCallback(() => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch {
      return null;
    }
  }, [token]);

  // Calcular delay de reconexión con backoff exponencial
  const getReconnectDelay = useCallback(() => {
    const baseDelay = 1000;
    const maxDelay = 30000;
    const delay = Math.min(baseDelay * Math.pow(2, reconnectAttempts.current), maxDelay);
    return delay + Math.random() * 1000; // Jitter
  }, []);

  // Conectar WebSocket
  const connect = useCallback(() => {
    const url = getWebSocketUrl();
    if (!url) {
      console.log('No URL for WebSocket');
      return;
    }

    // Limpiar conexión anterior
    if (wsRef.current) {
      wsRef.current.close();
    }

    setConnectionState(reconnectAttempts.current > 0 ? ConnectionState.RECONNECTING : ConnectionState.CONNECTING);

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket conectado');
      setConnectionState(ConnectionState.CONNECTED);
      setConnectionError(null);
      reconnectAttempts.current = 0;
      
      // Iniciar heartbeat con medición de latencia
      heartbeatIntervalRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          pingTimestamp.current = Date.now();
          sendMessage('heartbeat');
        }
      }, 15000);

      // Solicitar estado actual
      requestSessionInfo();
    };

    ws.onclose = (event) => {
      console.log('WebSocket desconectado', event.code, event.reason);
      setConnectionState(ConnectionState.DISCONNECTED);
      
      // Limpiar heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      
      // Reconectar si no fue cierre intencional y no excedimos intentos
      if (event.code !== 1000 && event.code !== 4001 && reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current++;
        const delay = getReconnectDelay();
        console.log(`Reconectando en ${delay}ms (intento ${reconnectAttempts.current}/${maxReconnectAttempts})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else if (reconnectAttempts.current >= maxReconnectAttempts) {
        setConnectionError('Se alcanzó el máximo de intentos de reconexión');
        setConnectionState(ConnectionState.ERROR);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionError('Error de conexión WebSocket');
      setConnectionState(ConnectionState.ERROR);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);

        // Track sequence for cross-pod replay on reconnect (RCA §48).
        if (typeof data.sequence_number === 'number' && data.sequence_number > lastSeqRef.current) {
          lastSeqRef.current = data.sequence_number;
          setLastSequenceNumber(data.sequence_number);
        }

        // Procesar según tipo de evento
        switch (data.event_type) {
          case 'connected':
            setSessionInfo(data.session_info);
            // Bug fix: also hydrate from `state` (full state_sync payload sent
            // on connect since v10.0.0). Previously only `session_info.vitals`
            // was read but that field is connection metadata, not state.
            if (data.state) {
              if (data.state.vitals && Object.keys(data.state.vitals).length) {
                setVitals(data.state.vitals);
              }
              if (data.state.patient_state) setPatientState(data.state.patient_state);
              if (typeof data.state.is_paused === 'boolean') setIsPaused(data.state.is_paused);
              if (typeof data.state.last_sequence_number === 'number') {
                lastSeqRef.current = data.state.last_sequence_number;
                setLastSequenceNumber(data.state.last_sequence_number);
              }
            } else if (data.session_info?.vitals) {
              setVitals(data.session_info.vitals);
            }
            break;

          case 'replay':
            // Cross-pod recovery: apply the events we missed while the socket
            // was on a different pod. Each event has already been persisted
            // in simulation_events.
            (data.events || []).forEach((ev) => {
              const t = ev.event_type;
              const extras = ev;
              const p = ev.payload || {};
              if (t === 'vitals_update' || t === 'vitals_modified_by_instructor' || t === 'clinical_event_activated' || t === 'action_performed') {
                if (extras.vitals || p.vitals) {
                  setVitals((prev) => ({ ...(prev || {}), ...(extras.vitals || p.vitals) }));
                }
                const ps = extras.patient_state || p.patient_state;
                if (ps) setPatientState(ps);
                if (t === 'action_performed') {
                  setTimeline((prev) => [...prev, ev]);
                }
              } else if (t === 'chat_message' || t === 'instructor_message') {
                setMessages((prev) => {
                  if (prev.some((m) => m.event_id === ev.event_id)) return prev;
                  return [...prev, { ...extras, ...p, event_id: ev.event_id }];
                });
              } else if (t === 'simulation_paused') {
                setIsPaused(true);
              } else if (t === 'simulation_resumed') {
                setIsPaused(false);
              }
              if (typeof ev.sequence_number === 'number') {
                lastSeqRef.current = Math.max(lastSeqRef.current, ev.sequence_number);
              }
            });
            break;
          
          case 'session_info':
            setSessionInfo(data);
            if (data.instructors) {
              setConnectedUsers(prev => ({ ...prev, instructors: data.instructors }));
            }
            if (data.students) {
              setConnectedUsers(prev => ({ ...prev, students: data.students }));
            }
            break;
          
          case 'vitals_update':
          case 'vitals_modified_by_instructor':
            // v10.3.1 Fase 2: capturar metadata de transición para que
            // el monitor del instructor también anime suavemente.
            if (data.transition) {
              setActiveVitalsTransition({
                from: data.transition.from || {},
                target: data.vitals,
                duration_ms: data.transition.duration_ms || 5000,
                started_at: data.transition.started_at,
                appliedAtMs: performance.now(),
              });
            } else {
              setActiveVitalsTransition(null);
            }
            setVitals(data.vitals);
            if (data.patient_state) {
              setPatientState(data.patient_state);
            }
            break;
          
          case 'patient_state_change':
            setPatientState(data.new_state);
            break;
          
          case 'chat_message':
            setMessages(prev => [...prev, { ...data, isHint: false }]);
            break;
          
          case 'instructor_hint':
          case 'instructor_message':
            setMessages(prev => [...prev, { ...data, isHint: data.event_type === 'instructor_hint' }]);
            break;
          
          case 'mode_change':
            const userId = getCurrentUserId();
            if (data.instructor_id === userId) {
              setSupervisionMode(data.new_mode);
              setHasControl(data.has_control);
            }
            // Actualizar lista de usuarios
            requestSessionInfo();
            break;
          
          case 'control_response':
            if (data.success) {
              setHasControl(data.action === 'take_control');
              if (data.action === 'take_control') {
                setSupervisionMode('control_total');
              }
            }
            break;
          
          case 'mode_response':
            if (data.success) {
              setSupervisionMode(data.mode);
              setHasControl(data.mode === 'control_total');
            }
            break;
          
          case 'user_joined':
          case 'user_left':
            requestSessionInfo();
            break;
          
          case 'action_performed':
            setTimeline(prev => [...prev, {
              id: data.id || `action-${Date.now()}`,
              timestamp: data.timestamp,
              elapsed_seconds: data.elapsed_seconds,
              event_type: 'action',
              actor: data.performed_by_name,
              action: data.action_type,
              actor_role: data.actor_role,
              action_category: data.action_category,
              medication: data.medication,
              action_data: data.action,
              details: data.details
            }]);
            // v10.5.0 regresión fix: poblar arrays LIVE cuando el payload
            // incluye estructura {medication|action|exam}. Sin esto el
            // instructor solo veía la entrada en la Cronología pero la
            // pestaña "Medicamentos"/"Acciones" quedaba vacía.
            if (data.medication || data.action_category === 'medication') {
              const med = data.medication || {};
              setLiveMedications(prev => {
                const entry = {
                  id: `wsm-${data.id || Date.now()}`,
                  nombre: med.nombre,
                  medication_name: med.nombre,
                  dosis: med.dosis,
                  dose: med.dosis,
                  unidad: med.unidad,
                  unit: med.unidad,
                  via: med.via,
                  route: med.via,
                  actor: data.performed_by_name,
                  actor_role: data.actor_role,
                  timestamp: data.timestamp,
                  elapsed_seconds: data.elapsed_seconds,
                  _source: 'ws_live',
                };
                if (prev.some(p => p.id === entry.id)) return prev;
                return [...prev, entry];
              });
            }
            if (data.action || data.action_category === 'action') {
              const act = data.action || {};
              setLiveActions(prev => {
                const entry = {
                  id: `wsa-${data.id || Date.now()}`,
                  nombre: act.nombre,
                  name: act.nombre,
                  categoria: act.categoria,
                  category: act.categoria,
                  actor: data.performed_by_name,
                  actor_role: data.actor_role,
                  timestamp: data.timestamp,
                  elapsed_seconds: data.elapsed_seconds,
                  _source: 'ws_live',
                };
                if (prev.some(p => p.id === entry.id)) return prev;
                return [...prev, entry];
              });
            }
            // Bug fix P0 (v10.2.2): action_performed may carry merged vitals
            // (e.g. instructor administers epinephrine → FC↑). Update the
            // instructor's own monitor so it stays in sync with Mongo state.
            if (data.vitals && Object.keys(data.vitals).length) {
              setVitals(data.vitals);
            }
            if (data.patient_state) {
              setPatientState(data.patient_state);
            }
            break;
          
          case 'clinical_event_activated':
            setTimeline(prev => [...prev, {
              id: `event-${Date.now()}`,
              timestamp: data.timestamp,
              event_type: 'clinical_event',
              actor: 'instructor',
              action: data.event_name,
              details: { description: data.description }
            }]);
            if (data.vitals) {
              setVitals(data.vitals);
            }
            if (data.patient_state) {
              setPatientState(data.patient_state);
            }
            break;
          
          case 'simulation_paused':
            setIsPaused(true);
            break;

          case 'control_requested':
            // v10.2.4: alumno pidió intervención / liberación de control.
            // Guardamos el request para que la UI lo muestre; expira solo si
            // el instructor lo dismissea o cambia el control.
            setPendingControlRequest({
              student_id: data.student_id,
              student_name: data.student_name,
              reason: data.reason,
              controller_id: data.controller_id,
              timestamp: data.timestamp,
            });
            break;
          
          case 'time_extended':
            setTimeline(prev => [...prev, {
              id: `ext-${Date.now()}`,
              timestamp: data.timestamp,
              event_type: 'time_extended',
              actor: data.extended_by,
              action: `Tiempo extendido +${data.added_minutes} min`,
              details: { new_expires_at: data.new_expires_at }
            }]);
            break;
          
          case 'session_timeout':
            setTimeline(prev => [...prev, {
              id: `timeout-${Date.now()}`,
              timestamp: data.timestamp,
              event_type: 'session_timeout',
              actor: 'sistema',
              action: 'Tiempo agotado — simulación finalizada por el servidor',
              details: {}
            }]);
            break;
          
          case 'simulation_resumed':
            setIsPaused(false);
            break;
          
          case 'simulation_ended_by_instructor':
            // Manejar finalización
            break;
          
          case 'heartbeat_ack':
          case 'pong':
            if (pingTimestamp.current) {
              setLatency(Date.now() - pingTimestamp.current);
              pingTimestamp.current = null;
            }
            break;
          
          case 'control_transfer_request':
            // Notificar al UI de solicitud de transferencia
            break;
          
          default:
            // v10.8.1 — Interceptar eventos XR (categoría xr) para el panel del instructor
            if (typeof data.event_type === 'string' && data.event_type.startsWith('xr:')) {
              const xrEv = {
                event_id: data.event_id,
                event_type: data.event_type,
                target: data.target || (data.payload || {}).target || {},
                actor: data.actor,
                category: 'xr',
                timestamp: data.timestamp || new Date().toISOString(),
                sequence_number: data.sequence_number,
              };
              setLastXrEvent(xrEv);
              setXrEvents((prev) => [xrEv, ...prev].slice(0, 30));
              break;
            }
            console.log('Evento no manejado:', data.event_type, data);
            break;
        }
      } catch (e) {
        console.error('Error parseando mensaje WebSocket:', e, event.data);
      }
    };
  }, [getWebSocketUrl, sendMessage, requestSessionInfo, getCurrentUserId, getReconnectDelay]);

  // Desconectar
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Cierre intencional');
      wsRef.current = null;
    }
    setConnectionState(ConnectionState.DISCONNECTED);
    reconnectAttempts.current = 0;
  }, []);

  // Reconectar manualmente
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttempts.current = 0;
    setTimeout(connect, 100);
  }, [disconnect, connect]);

  // Limpiar mensajes
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Conectar al montar si hay sessionId y token
  useEffect(() => {
    if (sessionId && token) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [sessionId, token, connect, disconnect]);

  // Cross-pod REST backplane (RCA §48): mirror the student hook's polling so
  // an instructor also sees student actions/medications/exams when they land
  // on different pods. WS remains the primary channel; this is a safety net.
  useEffect(() => {
    if (!sessionId || !token) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const url = `${BACKEND_URL}/api/supervision/session/${sessionId}/events?since=${lastSeqRef.current || 0}`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        if (d.current_state) {
          const cs = d.current_state;
          if (cs.vitals && Object.keys(cs.vitals).length) setVitals(cs.vitals);
          if (cs.patient_state) setPatientState(cs.patient_state);
          if (typeof cs.is_paused === 'boolean') setIsPaused(cs.is_paused);
        }
        (d.events || []).forEach((ev) => {
          const t = ev.event_type;
          const extras = ev;
          const p = ev.payload || {};
          if (t === 'vitals_update' || t === 'vitals_modified_by_instructor' || t === 'clinical_event_activated' || t === 'action_performed') {
            if (extras.vitals || p.vitals) {
              setVitals((prev) => ({ ...(prev || {}), ...(extras.vitals || p.vitals) }));
            }
            const ps = extras.patient_state || p.patient_state;
            if (ps) setPatientState(ps);
            if (t === 'action_performed') {
              setTimeline((prev) => {
                if (prev.some((x) => x.event_id === ev.event_id)) return prev;
                return [...prev, { ...ev, event_id: ev.event_id }];
              });
            }
          } else if (t === 'chat_message' || t === 'instructor_message') {
            setMessages((prev) => {
              if (prev.some((m) => m.event_id === ev.event_id)) return prev;
              return [...prev, { ...extras, ...p, event_id: ev.event_id }];
            });
          } else if (t === 'simulation_paused') {
            setIsPaused(true);
          } else if (t === 'simulation_resumed') {
            setIsPaused(false);
          }
        });
        if (typeof d.last_sequence_number === 'number') {
          lastSeqRef.current = Math.max(lastSeqRef.current, d.last_sequence_number);
          setLastSequenceNumber(lastSeqRef.current);
        }
      } catch (_e) {
        // Silent — this is a backup channel.
      }
    };
    const iv = setInterval(tick, 4000);
    tick();
    return () => { cancelled = true; clearInterval(iv); };
  }, [sessionId, token]);

  return {
    // Estado de conexión
    connectionState,
    isConnected: connectionState === ConnectionState.CONNECTED,
    isConnecting: connectionState === ConnectionState.CONNECTING || connectionState === ConnectionState.RECONNECTING,
    connectionError,
    latency,
    
    // Datos de la sesión
    sessionInfo,
    connectedUsers,
    
    // Datos en tiempo real
    messages,
    vitals,
    patientState,
    timeline,
    liveMedications,
    liveActions,
    liveExams,
    isPaused,
    
    // Estado de supervisión
    supervisionMode,
    hasControl,

    // v10.2.4 — pedidos de control del alumno
    pendingControlRequest,
    dismissControlRequest: useCallback(() => setPendingControlRequest(null), []),
    // v10.3.1 Fase 2 — transición progresiva de vitales
    activeVitalsTransition,
    setActiveVitalsTransition,

    // v10.8.1 — XR Live (eventos XR del alumno para el instructor)
    xrEvents,
    lastXrEvent,
    
    // Último mensaje recibido
    lastMessage,
    
    // Acciones
    sendMessage,
    sendChatMessage,
    sendHint,
    takeControl,
    releaseControl,
    setMode,
    requestSessionInfo,
    requestStateSync,
    sendVitalsUpdate,
    sendAction,
    pauseSimulation,
    resumeSimulation,
    endSimulation,
    triggerEvent,
    
    // Control de conexión
    connect,
    disconnect,
    reconnect,
    clearMessages,
    
    // Utilidades
    getCurrentUserId
  };
};

export default useSupervisionWebSocketV2;
